import React, { Component } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import esLocale from '@fullcalendar/core/locales/es';
import "@fullcalendar/daygrid/main.css";
import "@fullcalendar/timegrid/main.css";
// Etiquetas de origen compartidas con el historial de configuración.
import { SOURCE_LABELS, SOURCE_ICONS } from '../shared/auditSources';


class FormatCalendar extends Component {
    constructor(props) {
        super(props);
        this.token = document.querySelector("[name='csrf-token']").content;

        // Recordar el mes visible al abrir un registro para restaurarlo al volver.
        // Se usa sessionStorage (el redirect del form de edición v1 no arrastra
        // params custom). One-shot: al leerlo se borra.
        this.returnKey = `bw_cal_return_${props.formato && props.formato.id}`;
        this.currentViewDate = null;
        let initialDate = null;
        try {
            initialDate = sessionStorage.getItem(this.returnKey);
            if (initialDate) sessionStorage.removeItem(this.returnKey);
        } catch (_) { /* sessionStorage no disponible */ }

        this.state = {
            events: [],
            isLoaded: true,
            modalOpen: false,
            selected: null,      // { id, title, start, end, status, responsible, url }
            audits: [],
            loadingAudits: false,
            range: null,         // { start, end } del rango visible (lo da datesSet)
            initialDate,         // mes a mostrar al montar (si venimos de editar)
        };
    }

    // No cargamos en didMount: FullCalendar dispara datesSet al montar (y en cada
    // cambio de vista/navegación) → ahí pedimos SOLO el rango visible.

    // En modo embebido la URL base cambia al aplicar filtros/búsqueda → recargar
    // el rango actualmente visible con los nuevos filtros.
    componentDidUpdate(prevProps) {
        const url = this.props.dataUrl || this.props.data_url;
        const prevUrl = prevProps.dataUrl || prevProps.data_url;
        if (url !== prevUrl && this.state.range) {
            this.loadData(this.state.range.start, this.state.range.end);
        }
    }

    // FullCalendar: al montar y cada vez que cambia el rango visible (mes/semana/
    // día o prev/next). Pedimos solo ese rango.
    handleDatesSet = (info) => {
        const start = info.startStr, end = info.endStr;
        // Mes/periodo "actual" de la vista (el 1° en vista Mes) — para restaurarlo al volver.
        const d = info.view && info.view.currentStart;
        if (d) {
            this.currentViewDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        }
        this.setState({ range: { start, end } });
        this.loadData(start, end);
    }

    // Antes de navegar a editar, guardamos el mes visible para restaurarlo al volver.
    rememberViewDate = () => {
        try { if (this.currentViewDate) sessionStorage.setItem(this.returnKey, this.currentViewDate); } catch (_) {}
    }

    loadData = (start, end) => {
        let url = this.props.dataUrl || this.props.data_url;
        if (start && end) {
            const sep = url.includes("?") ? "&" : "?";
            url = `${url}${sep}start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`;
        }
        fetch(url, {
            headers: { "X-CSRF-Token": this.token, "Content-Type": "application/json" }
        })
            .then(r => r.json())
            .then(data => this.setState({ events: data.events || [], isLoaded: false }));
    }

    // Click → abrir modal con info + historial (no navega directo).
    handleEventClick = (info) => {
        info.jsEvent.preventDefault();
        const e = info.event;
        const selected = {
            id: e.id,
            title: e.title,
            start: e.start,
            end: e.end,
            allDay: e.allDay,
            status: e.extendedProps.status,
            responsible: e.extendedProps.responsible,
            url: e.url,
        };
        this.setState({ modalOpen: true, selected, audits: [], loadingAudits: true });
        this.loadAudits(e.id);
    }

    loadAudits = (surveyId) => {
        fetch(`/formatos/get_survey_audits/${surveyId}`, {
            headers: { "X-CSRF-Token": this.token, "Content-Type": "application/json" }
        })
            .then(r => r.json())
            .then(data => this.setState({ audits: data.audits || [], loadingAudits: false }))
            .catch(() => this.setState({ loadingAudits: false }));
    }

    closeModal = () => this.setState({ modalOpen: false, selected: null, audits: [] });

    // Drag (mover) o resize (estirar) → reescribe la fecha del registro.
    handleEventChange = (info) => {
        const e = info.event;
        fetch(`/formatos/records/${this.props.formato.id}/calendar_event/${e.id}`, {
            method: "PATCH",
            body: JSON.stringify({
                start: e.startStr || null,
                end: e.endStr || null,
                all_day: e.allDay
            }),
            headers: { "X-CSRF-Token": this.token, "Content-Type": "application/json" }
        })
            .then(r => r.json())
            .then(data => { if (!data || data.type === "error") info.revert(); })
            .catch(() => info.revert());
    }

    // Fecha en español + hora en formato 12h AM/PM. En eventos de todo el día
    // se omite la hora (no tiene sentido mostrar 12:00 AM).
    fmtDateTime = (d, allDay = false) => {
        if (!d) return "—";
        try {
            const date = new Date(d);
            const fecha = date.toLocaleDateString("es", { day: "numeric", month: "short", year: "numeric" });
            if (allDay) return fecha;
            const hora = date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
            return `${fecha} · ${hora}`;
        } catch (_) { return String(d); }
    }

    renderEventContent = (arg) => {
        const owner = arg.event.extendedProps.responsible;
        const status = arg.event.extendedProps.status;
        // Coloreamos el contenido interno con el color del evento: el .fc-event
        // externo lo deja transparente el SCSS (un calendar.scss global lo forzaba
        // a celeste con !important, pisando el color por estado/semáforo).
        const bg = arg.event.backgroundColor || arg.backgroundColor;
        return (
            <div className="bw-fc-event" style={bg ? { backgroundColor: bg } : undefined}>
                {arg.timeText && <span className="bw-fc-event__time">{arg.timeText}</span>}
                <span className="bw-fc-event__title">{arg.event.title}</span>
                {owner && <span className="bw-fc-event__meta">{owner}</span>}
                {status && <span className="bw-fc-event__meta">{status}</span>}
            </div>
        );
    }

    renderHistory() {
        const { loadingAudits, audits } = this.state;
        if (loadingAudits) return <div className="text-muted">Cargando historial...</div>;
        if (!audits.length) return <div className="text-muted">Sin modificaciones registradas.</div>;

        return (
            <div className="bw-cal-history">
                {audits.map(a => (
                    <div key={a.id} className="bw-cal-history__item">
                        <div className="bw-cal-history__head">
                            <i className={`${SOURCE_ICONS[a.source] || 'fas fa-pen'} mr-1`} />
                            <b>{a.user}</b>
                            {SOURCE_LABELS[a.source] && (
                                <span className="badge badge-light ml-1" title="Origen del cambio">{SOURCE_LABELS[a.source]}</span>
                            )}
                            {' '}· <span className="text-muted">{a.created_at}</span>
                        </div>
                        <ul className="bw-cal-history__changes">
                            {a.changes.map((c, i) => (
                                <li key={i}>
                                    <span className="bw-cal-history__q">{c.question}:</span>{' '}
                                    <span className="bw-cal-history__before">{c.before || "(vacío)"}</span>
                                    {' → '}
                                    <span className="bw-cal-history__after">{c.after || "(vacío)"}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
        );
    }

    render() {
        const { selected } = this.state;
        const embedded = !!this.props.embedded;
        return (
            <div className={`bw-format-calendar${embedded ? " bw-format-calendar--embedded" : ""}`}>
                {!embedded && (
                    <div className="bw-format-calendar__header">
                        <div>
                            <a href={this.props.records_url} className="btn btn-sm btn-outline-secondary mb-2">
                                <i className="fas fa-arrow-left mr-1"></i> Volver a registros
                            </a>
                            <h4 className="bw-format-calendar__title">{this.props.formato.name}</h4>
                            <span className="bw-format-calendar__subtitle">{this.props.format_category.name} · Calendario</span>
                        </div>
                    </div>
                )}

                <div className="bw-calendar-wrapper">
                    <FullCalendar
                        initialView="dayGridMonth"
                        initialDate={this.state.initialDate || undefined}
                        locale={esLocale}
                        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                        events={this.state.events}
                        eventClick={this.handleEventClick}
                        datesSet={this.handleDatesSet}
                        eventContent={this.renderEventContent}
                        editable={true}
                        eventDrop={this.handleEventChange}
                        eventResize={this.handleEventChange}
                        headerToolbar={{
                            left: "prev,next today",
                            center: "title",
                            right: "dayGridMonth,timeGridWeek,timeGridDay"
                        }}
                        buttonText={{ today: "Hoy", month: "Mes", week: "Semana", day: "Día" }}
                        slotMinTime="06:00:00"
                        slotMaxTime="21:00:00"
                        nowIndicator={true}
                        allDaySlot={true}
                        allDayText="Todo el día"
                        dayMaxEvents={3}
                        eventTimeFormat={{ hour: "numeric", minute: "2-digit", meridiem: "short", hour12: true }}
                        height="100%"
                        expandRows={true}
                    />
                </div>

                <Modal isOpen={this.state.modalOpen} toggle={this.closeModal} size="lg">
                    <ModalHeader toggle={this.closeModal}>
                        {selected ? selected.title : "Registro"}
                    </ModalHeader>
                    <ModalBody>
                        {selected && (
                            <div className="bw-cal-modal">
                                <div className="bw-cal-modal__info">
                                    <div><b>Inicio:</b> {this.fmtDateTime(selected.start, selected.allDay)}</div>
                                    <div><b>Fin:</b> {this.fmtDateTime(selected.end, selected.allDay)}</div>
                                    {selected.status && <div><b>Estado:</b> {selected.status}</div>}
                                    {selected.responsible && <div><b>Responsable:</b> {selected.responsible}</div>}
                                </div>

                                <hr />
                                <h6 style={{ fontWeight: 700 }}>Historial de cambios</h6>
                                {this.renderHistory()}
                            </div>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        {selected && selected.url && (
                            <a href={selected.url} onClick={this.rememberViewDate} className="btn btn-info">
                                <i className="fas fa-edit mr-1" /> Editar registro
                            </a>
                        )}
                        <button className="btn btn-outline-danger" onClick={this.closeModal}>Cerrar</button>
                    </ModalFooter>
                </Modal>
            </div>
        );
    }
}

export default FormatCalendar;
